/**
 * Storage Service
 *
 * Service for managing local storage with dependency injection for storage provider.
 */

import { UserProgress, SkillLevels } from '../../types/progress';

/**
 * Current schema version for local persistence envelopes.
 */
export const LOCAL_STORAGE_SCHEMA_VERSION = 2;

/**
 * Constants for localStorage keys
 */
export const LOCAL_STORAGE_KEYS = {
  USER_PROGRESS: 'chessCodebreaker.userProgress',
  SETTINGS: 'chessCodebreaker.settings',
  ACHIEVEMENTS: 'chessCodebreaker.achievements',
  PROFILE: 'chessCodebreaker.profile',
};

/**
 * Generic versioned envelope for persisted values.
 */
export interface VersionedEnvelope<T> {
  storageVersion: number;
  data: T;
}

/**
 * Consolidated local profile envelope (single-device profile).
 */
export interface LocalProfile {
  storageVersion: number;
  userProgress?: VersionedEnvelope<UserProgress>;
  achievements?: VersionedEnvelope<string[]>;
  knowledgeItems?: VersionedEnvelope<unknown[]>;
}

/**
 * Interface for storage provider
 * Matches the relevant methods of localStorage for easy substitution
 */
export interface StorageProvider {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Default storage provider that uses browser's localStorage
 */
export const defaultStorageProvider: StorageProvider = {
  getItem: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Error reading from storage: ${key}`, error);
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Error writing to storage: ${key}`, error);
    }
  },
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from storage: ${key}`, error);
    }
  }
};

/**
 * Type for user progress updates with partial skill levels.
 */
export interface UserProgressUpdate extends Omit<Partial<UserProgress>, 'skillLevels'> {
  skillLevels?: Partial<SkillLevels>;
  unlockedAchievements?: string[];
}

/**
 * Default user progress structure
 *
 * Used as a fallback when no existing progress is found
 */
export const defaultUserProgress: UserProgress = {
  currentStage: 1,
  completedMissions: [],
  skillLevels: {
    coordinates: 1,
    basicMoves: 1,
    sequences: 1,
    gameAnalysis: 1,
    castling: 1,
    pawnPromotion: 1,
    enPassant: 1,
    pieceMoves: {
      pawn: 1,
      knight: 1,
      bishop: 1,
      rook: 1,
      queen: 1,
      king: 1,
    }
  },
  statistics: {
    totalMissionsCompleted: 0,
    totalChallengesCompleted: 0,
    averageAccuracy: 0,
    bestScore: 0,
    lastActive: new Date().toISOString(),
  },
  missionResults: [],
  unlockedAchievements: [],
  moveSkillMetrics: {
    totalMoves: {
      attempted: 0,
      successful: 0,
    },
    movesByType: {
      normal: { attempted: 0, successful: 0 },
      castling: { attempted: 0, successful: 0 },
      enPassant: { attempted: 0, successful: 0 },
      promotion: { attempted: 0, successful: 0 },
    },
    movesByPiece: {
      pawn: { attempted: 0, successful: 0 },
      knight: { attempted: 0, successful: 0 },
      bishop: { attempted: 0, successful: 0 },
      rook: { attempted: 0, successful: 0 },
      queen: { attempted: 0, successful: 0 },
      king: { attempted: 0, successful: 0 },
    },
    averageTimePerMove: 0,
    hintsUsed: 0,
  },
};

const asVersioned = <T>(data: T): VersionedEnvelope<T> => ({
  storageVersion: LOCAL_STORAGE_SCHEMA_VERSION,
  data,
});

const isVersionedEnvelope = <T>(value: unknown): value is VersionedEnvelope<T> => {
  return !!value && typeof value === 'object' && 'storageVersion' in value && 'data' in value;
};

const safeParseJSON = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const sanitizeUserProgress = (input: Partial<UserProgress> | null | undefined): UserProgress => {
  if (!input || typeof input !== 'object') {
    return { ...defaultUserProgress };
  }

  const mergedPieceMoves = {
    ...defaultUserProgress.skillLevels.pieceMoves,
    ...(input.skillLevels?.pieceMoves || {}),
  } as NonNullable<UserProgress['skillLevels']['pieceMoves']>;

  const mergedMoveSkillMetrics = {
    ...defaultUserProgress.moveSkillMetrics!,
    ...(input.moveSkillMetrics || {}),
    totalMoves: {
      ...defaultUserProgress.moveSkillMetrics!.totalMoves,
      ...(input.moveSkillMetrics?.totalMoves || {}),
    },
    movesByType: {
      ...defaultUserProgress.moveSkillMetrics!.movesByType,
      ...(input.moveSkillMetrics?.movesByType || {}),
    },
    movesByPiece: {
      ...defaultUserProgress.moveSkillMetrics!.movesByPiece,
      ...(input.moveSkillMetrics?.movesByPiece || {}),
    },
    averageTimePerMove: input.moveSkillMetrics?.averageTimePerMove ?? defaultUserProgress.moveSkillMetrics!.averageTimePerMove,
    hintsUsed: input.moveSkillMetrics?.hintsUsed ?? defaultUserProgress.moveSkillMetrics!.hintsUsed,
  };

  return {
    ...defaultUserProgress,
    ...input,
    skillLevels: {
      ...defaultUserProgress.skillLevels,
      ...(input.skillLevels || {}),
      pieceMoves: mergedPieceMoves,
    },
    statistics: {
      ...defaultUserProgress.statistics,
      ...(input.statistics || {}),
      lastActive: input.statistics?.lastActive || new Date().toISOString(),
    },
    completedMissions: Array.isArray(input.completedMissions) ? input.completedMissions : [],
    missionResults: Array.isArray(input.missionResults) ? input.missionResults : [],
    unlockedAchievements: Array.isArray(input.unlockedAchievements) ? input.unlockedAchievements : [],
    moveSkillMetrics: mergedMoveSkillMetrics,
  };
};

const sanitizeAchievements = (input: unknown): string[] => {
  if (!Array.isArray(input)) return [];
  return input.filter((v): v is string => typeof v === 'string');
};

/**
 * Read consolidated local profile from storage.
 */
export const readLocalProfile = (
  storage: StorageProvider = defaultStorageProvider
): LocalProfile | null => {
  const parsed = safeParseJSON<LocalProfile>(storage.getItem(LOCAL_STORAGE_KEYS.PROFILE));
  if (!parsed || typeof parsed !== 'object') return null;
  return parsed;
};

/**
 * Write consolidated local profile to storage.
 */
export const writeLocalProfile = (
  profile: LocalProfile,
  storage: StorageProvider = defaultStorageProvider
): void => {
  storage.setItem(LOCAL_STORAGE_KEYS.PROFILE, JSON.stringify(profile));
};

/**
 * Idempotent migration into consolidated local profile envelope.
 */
export const migrateLocalProfile = (
  storage: StorageProvider = defaultStorageProvider
): LocalProfile => {
  const existingProfile = readLocalProfile(storage);

  if (
    existingProfile &&
    existingProfile.storageVersion >= LOCAL_STORAGE_SCHEMA_VERSION &&
    existingProfile.userProgress &&
    existingProfile.achievements
  ) {
    return existingProfile;
  }

  const legacyProgressRaw = safeParseJSON<unknown>(storage.getItem(LOCAL_STORAGE_KEYS.USER_PROGRESS));
  const legacyAchievementsRaw = safeParseJSON<unknown>(storage.getItem(LOCAL_STORAGE_KEYS.ACHIEVEMENTS));

  const hasProfileProgress = !!existingProfile?.userProgress;
  const hasProfileAchievements = !!existingProfile?.achievements;

  const progressFromProfile = isVersionedEnvelope<UserProgress>(existingProfile?.userProgress)
    ? existingProfile!.userProgress.data
    : sanitizeUserProgress((existingProfile?.userProgress as unknown as Partial<UserProgress>) || null);

  const achievementsFromProfile = isVersionedEnvelope<string[]>(existingProfile?.achievements)
    ? sanitizeAchievements(existingProfile!.achievements.data)
    : sanitizeAchievements(existingProfile?.achievements);

  const progressFromLegacy = isVersionedEnvelope<UserProgress>(legacyProgressRaw)
    ? sanitizeUserProgress(legacyProgressRaw.data)
    : sanitizeUserProgress(legacyProgressRaw as Partial<UserProgress>);

  const achievementsFromLegacy = isVersionedEnvelope<string[]>(legacyAchievementsRaw)
    ? sanitizeAchievements(legacyAchievementsRaw.data)
    : sanitizeAchievements(legacyAchievementsRaw);

  const nextProfile: LocalProfile = {
    storageVersion: LOCAL_STORAGE_SCHEMA_VERSION,
    userProgress: asVersioned(hasProfileProgress ? progressFromProfile : progressFromLegacy),
    achievements: asVersioned(hasProfileAchievements ? achievementsFromProfile : achievementsFromLegacy),
    knowledgeItems: existingProfile?.knowledgeItems,
  };

  writeLocalProfile(nextProfile, storage);

  // Keep legacy keys synchronized for backwards compatibility.
  storage.setItem(LOCAL_STORAGE_KEYS.USER_PROGRESS, JSON.stringify(nextProfile.userProgress));
  storage.setItem(LOCAL_STORAGE_KEYS.ACHIEVEMENTS, JSON.stringify(nextProfile.achievements));

  return nextProfile;
};

/**
 * Get user progress from storage.
 */
export const getUserProgress = (
  storage: StorageProvider = defaultStorageProvider
): UserProgress => {
  try {
    const profile = migrateLocalProfile(storage);
    const fromProfile = profile.userProgress;

    if (isVersionedEnvelope<UserProgress>(fromProfile)) {
      return sanitizeUserProgress(fromProfile.data);
    }

    const legacyStored = safeParseJSON<unknown>(storage.getItem(LOCAL_STORAGE_KEYS.USER_PROGRESS));
    if (isVersionedEnvelope<UserProgress>(legacyStored)) {
      return sanitizeUserProgress(legacyStored.data);
    }

    return sanitizeUserProgress(legacyStored as Partial<UserProgress>);
  } catch (error) {
    console.error('Error loading user progress:', error);
    return { ...defaultUserProgress };
  }
};

/**
 * Get unlocked achievements from storage.
 */
export const getUnlockedAchievements = (
  storage: StorageProvider = defaultStorageProvider
): string[] => {
  try {
    const profile = migrateLocalProfile(storage);

    if (isVersionedEnvelope<string[]>(profile.achievements)) {
      return sanitizeAchievements(profile.achievements.data);
    }

    const legacyStored = safeParseJSON<unknown>(storage.getItem(LOCAL_STORAGE_KEYS.ACHIEVEMENTS));
    if (isVersionedEnvelope<string[]>(legacyStored)) {
      return sanitizeAchievements(legacyStored.data);
    }

    return sanitizeAchievements(legacyStored);
  } catch (error) {
    console.error('Error loading achievements:', error);
    return [];
  }
};

/**
 * Save user progress to storage.
 */
export const saveUserProgress = (
  progress: UserProgress,
  storage: StorageProvider = defaultStorageProvider
): void => {
  try {
    const updatedProgress = sanitizeUserProgress({
      ...progress,
      statistics: {
        ...progress.statistics,
        lastActive: new Date().toISOString(),
      }
    });

    const profile = migrateLocalProfile(storage);
    const nextProfile: LocalProfile = {
      ...profile,
      storageVersion: LOCAL_STORAGE_SCHEMA_VERSION,
      userProgress: asVersioned(updatedProgress),
      achievements: asVersioned(sanitizeAchievements(progress.unlockedAchievements || profile.achievements?.data || [])),
    };

    writeLocalProfile(nextProfile, storage);

    // Legacy mirror for backward compatibility.
    storage.setItem(LOCAL_STORAGE_KEYS.USER_PROGRESS, JSON.stringify(nextProfile.userProgress));
    storage.setItem(LOCAL_STORAGE_KEYS.ACHIEVEMENTS, JSON.stringify(nextProfile.achievements));
  } catch (error) {
    console.error('Error saving user progress:', error);
  }
};

/**
 * Update specific user progress fields.
 */
export const updateUserProgress = (
  updates: UserProgressUpdate,
  storage: StorageProvider = defaultStorageProvider
): UserProgress => {
  const currentProgress = getUserProgress(storage);

  const updatedProgress = sanitizeUserProgress({
    ...currentProgress,
    ...updates,
    skillLevels: {
      ...currentProgress.skillLevels,
      ...(updates.skillLevels || {}),
      pieceMoves: ({
        ...currentProgress.skillLevels.pieceMoves,
        ...(updates.skillLevels?.pieceMoves || {}),
      } as NonNullable<UserProgress['skillLevels']['pieceMoves']>),
    },
    statistics: {
      ...currentProgress.statistics,
      ...(updates.statistics || {}),
    },
    unlockedAchievements: updates.unlockedAchievements || currentProgress.unlockedAchievements || [],
  });

  saveUserProgress(updatedProgress, storage);
  return updatedProgress;
};

/**
 * Clear user progress data (for testing or reset functionality).
 */
export const clearUserProgress = (
  storage: StorageProvider = defaultStorageProvider
): void => {
  try {
    storage.removeItem(LOCAL_STORAGE_KEYS.USER_PROGRESS);
    storage.removeItem(LOCAL_STORAGE_KEYS.ACHIEVEMENTS);
    storage.removeItem(LOCAL_STORAGE_KEYS.PROFILE);
  } catch (error) {
    console.error('Error clearing user progress:', error);
  }
};

// Export all functions as default object as well for backward compatibility
export default {
  getUserProgress,
  saveUserProgress,
  updateUserProgress,
  clearUserProgress,
  getUnlockedAchievements,
  migrateLocalProfile,
  readLocalProfile,
  writeLocalProfile,
  LOCAL_STORAGE_KEYS,
  LOCAL_STORAGE_SCHEMA_VERSION,
  defaultUserProgress,
  defaultStorageProvider
};
